/// <reference path="./grapesjs-plugins.d.ts" />
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@futuredev/shared-auth/api';
import { extractApiError } from '@futuredev/shared-system/formatters';
import type { Editor } from 'grapesjs';
import { applyContentToEditor, normalizeAiWebsiteHtml } from './editorContent';
import {
  DocumentArrowDownIcon,
  TrashIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CodeBracketIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  Squares2X2Icon,
  SwatchIcon,
  PaintBrushIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface PageData {
  id?: number;
  title?: string;
  slug?: string;
  status?: string;
  content?: string | PageContent;
}

interface PageContent {
  components?: unknown;
  styles?: unknown;
  css?: string;
  html?: string;
}

interface CodeContent {
  html: string;
  css: string;
}

interface PageTemplate {
  id: string;
  name: string;
  thumbnail: string;
  content: string;
}

interface AiGeneratedSite {
  html?: string;
  css?: string;
  siteName?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GrapesEditor = any;

const TAILWIND_CANVAS_SCRIPT = 'https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4';

// Static content with no dependency on props or state. Defined at module scope so
// it keeps a stable identity: as a value inside the component it was both
// reallocated on every render and a phantom dependency of initEditor, and adding
// it there would have torn down and rebuilt the GrapesJS editor on every render.
const PAGE_TEMPLATES: PageTemplate[] = [
  {
    id: 'blank',
    name: 'Blank Page',
    thumbnail: '📄',
    content: '<div class="min-h-screen p-8"><p>Start with a blank canvas</p></div>'
  },
  {
    id: 'landing',
    name: 'Landing Page',
    thumbnail: '🚀',
    content: `
      <div class="min-h-screen">
        <nav class="bg-white shadow-sm px-6 py-4">
          <div class="container mx-auto flex justify-between items-center">
            <a href="#" class="text-2xl font-bold text-blue-600">Brand</a>
            <div class="flex gap-6">
              <a href="#" class="text-gray-600 hover:text-blue-600">Features</a>
              <a href="#" class="text-gray-600 hover:text-blue-600">Pricing</a>
              <a href="#" class="text-gray-600 hover:text-blue-600">Contact</a>
              <a href="#" class="px-4 py-2 bg-blue-600 text-white rounded-lg">Get Started</a>
            </div>
          </div>
        </nav>
        <section class="py-20 bg-gradient-to-br from-blue-600 to-purple-700">
          <div class="container mx-auto px-6 text-center">
            <h1 class="text-5xl font-bold text-white mb-6">Build Something Amazing</h1>
            <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Create beautiful websites without writing code. Drag, drop, and customize to your heart's content.</p>
            <div class="flex gap-4 justify-center">
              <a href="#" class="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100">Start Free Trial</a>
              <a href="#" class="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10">Watch Demo</a>
            </div>
          </div>
        </section>
        <section class="py-20 bg-gray-50">
          <div class="container mx-auto px-6">
            <h2 class="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
            <div class="grid md:grid-cols-3 gap-8">
              <div class="bg-white p-8 rounded-xl shadow-sm text-center">
                <div class="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl">🎨</div>
                <h3 class="text-xl font-semibold mb-2">Beautiful Design</h3>
                <p class="text-gray-600">Professional templates and components to make your site stand out.</p>
              </div>
              <div class="bg-white p-8 rounded-xl shadow-sm text-center">
                <div class="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl">⚡</div>
                <h3 class="text-xl font-semibold mb-2">Lightning Fast</h3>
                <p class="text-gray-600">Optimized code for blazing fast load times and great SEO.</p>
              </div>
              <div class="bg-white p-8 rounded-xl shadow-sm text-center">
                <div class="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl">🔧</div>
                <h3 class="text-xl font-semibold mb-2">Easy to Customize</h3>
                <p class="text-gray-600">No coding required. Just drag, drop, and customize.</p>
              </div>
            </div>
          </div>
        </section>
        <section class="py-20 bg-blue-600">
          <div class="container mx-auto px-6 text-center">
            <h2 class="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p class="text-xl text-white/90 mb-8">Join thousands of happy customers today.</p>
            <a href="#" class="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100">Sign Up Free</a>
          </div>
        </section>
        <footer class="bg-gray-900 text-white py-12">
          <div class="container mx-auto px-6">
            <div class="grid md:grid-cols-4 gap-8">
              <div>
                <h4 class="text-lg font-semibold mb-4">Brand</h4>
                <p class="text-gray-400">Building the future of web design.</p>
              </div>
              <div>
                <h4 class="text-lg font-semibold mb-4">Product</h4>
                <ul class="space-y-2 text-gray-400">
                  <li><a href="#" class="hover:text-white">Features</a></li>
                  <li><a href="#" class="hover:text-white">Pricing</a></li>
                  <li><a href="#" class="hover:text-white">Templates</a></li>
                </ul>
              </div>
              <div>
                <h4 class="text-lg font-semibold mb-4">Company</h4>
                <ul class="space-y-2 text-gray-400">
                  <li><a href="#" class="hover:text-white">About</a></li>
                  <li><a href="#" class="hover:text-white">Blog</a></li>
                  <li><a href="#" class="hover:text-white">Careers</a></li>
                </ul>
              </div>
              <div>
                <h4 class="text-lg font-semibold mb-4">Support</h4>
                <ul class="space-y-2 text-gray-400">
                  <li><a href="#" class="hover:text-white">Help Center</a></li>
                  <li><a href="#" class="hover:text-white">Contact</a></li>
                  <li><a href="#" class="hover:text-white">Status</a></li>
                </ul>
              </div>
            </div>
            <div class="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 Brand. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    `
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    thumbnail: '🎨',
    content: `
      <div class="min-h-screen bg-gray-900 text-white">
        <nav class="fixed top-0 left-0 right-0 bg-gray-900/95 backdrop-blur z-50 px-6 py-4">
          <div class="container mx-auto flex justify-between items-center">
            <a href="#" class="text-2xl font-bold">John Doe</a>
            <div class="flex gap-6">
              <a href="#work" class="text-gray-300 hover:text-white">Work</a>
              <a href="#about" class="text-gray-300 hover:text-white">About</a>
              <a href="#contact" class="text-gray-300 hover:text-white">Contact</a>
            </div>
          </div>
        </nav>
        <section class="pt-32 pb-20 px-6">
          <div class="container mx-auto">
            <h1 class="text-6xl font-bold mb-6">Creative Developer<br/>& Designer</h1>
            <p class="text-xl text-gray-400 max-w-xl">I build beautiful, functional websites and applications that help businesses grow.</p>
          </div>
        </section>
        <section id="work" class="py-20 px-6 bg-gray-800">
          <div class="container mx-auto">
            <h2 class="text-3xl font-bold mb-12">Selected Work</h2>
            <div class="grid md:grid-cols-2 gap-8">
              <div class="group relative overflow-hidden rounded-xl">
                <div class="aspect-video bg-gradient-to-br from-blue-500 to-purple-600"></div>
                <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span class="text-lg font-semibold">Project One</span>
                </div>
              </div>
              <div class="group relative overflow-hidden rounded-xl">
                <div class="aspect-video bg-gradient-to-br from-green-500 to-teal-600"></div>
                <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span class="text-lg font-semibold">Project Two</span>
                </div>
              </div>
              <div class="group relative overflow-hidden rounded-xl">
                <div class="aspect-video bg-gradient-to-br from-orange-500 to-red-600"></div>
                <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span class="text-lg font-semibold">Project Three</span>
                </div>
              </div>
              <div class="group relative overflow-hidden rounded-xl">
                <div class="aspect-video bg-gradient-to-br from-pink-500 to-rose-600"></div>
                <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span class="text-lg font-semibold">Project Four</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="contact" class="py-20 px-6">
          <div class="container mx-auto text-center">
            <h2 class="text-3xl font-bold mb-4">Let's Work Together</h2>
            <p class="text-gray-400 mb-8">Have a project in mind? Let's talk.</p>
            <a href="mailto:hello@example.com" class="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100">Get In Touch</a>
          </div>
        </section>
      </div>
    `
  },
  {
    id: 'blog',
    name: 'Blog Post',
    thumbnail: '📝',
    content: `
      <article class="min-h-screen bg-white">
        <header class="py-12 bg-gray-50">
          <div class="container mx-auto px-6 max-w-3xl">
            <div class="flex gap-2 mb-4">
              <span class="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">Technology</span>
              <span class="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">5 min read</span>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 mb-4">How to Build a Modern Website in 2024</h1>
            <p class="text-xl text-gray-600 mb-6">A comprehensive guide to creating fast, accessible, and beautiful websites.</p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 bg-gray-300 rounded-full"></div>
              <div>
                <p class="font-semibold text-gray-900">Jane Smith</p>
                <p class="text-sm text-gray-500">Published Jan 15, 2024</p>
              </div>
            </div>
          </div>
        </header>
        <div class="container mx-auto px-6 py-12 max-w-3xl">
          <div class="prose prose-lg">
            <p class="text-gray-700 leading-relaxed mb-6">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.</p>
            <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Getting Started</h2>
            <p class="text-gray-700 leading-relaxed mb-6">Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident.</p>
            <blockquote class="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-8">"The best way to predict the future is to create it." - Peter Drucker</blockquote>
            <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Key Takeaways</h2>
            <ul class="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Focus on user experience first</li>
              <li>Optimize for performance</li>
              <li>Make it accessible to everyone</li>
              <li>Test across different devices</li>
            </ul>
            <p class="text-gray-700 leading-relaxed">Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam.</p>
          </div>
        </div>
      </article>
    `
  }
];

const CANVAS_BASE_STYLES = [
  'data:text/css,body{margin:0;padding:0;min-height:100%;width:100%;}',
];

export interface WebsiteEditorApi {
  getHtml: () => string;
  getCss: () => string;
  setContent: (html: string, css?: string) => void;
}

export interface WebsiteBuilderCanvasProps {
  pageId?: string;
  onPageIdChange?: (newId: string) => void;
  isDark?: boolean;
  initialAiContent?: AiGeneratedSite | null;
  showBackLink?: boolean;
  backLinkTo?: string;
  backLinkLabel?: string;
  onEditorReady?: (api: WebsiteEditorApi) => void;
  compactChrome?: boolean;
  /** When true, fill a parent flex column instead of using viewport height. */
  fillContainer?: boolean;
  /** Parent-owned html/css; skips API load when embeddedMode is set. */
  externalProject?: { html: string; css: string } | null;
  embeddedMode?: boolean;
  /** Increment to re-import externalProject into the canvas (e.g. when opening Visual tab). */
  visualSyncKey?: number;
  /** Hide in-canvas save; parent persists project files. */
  delegateSave?: boolean;
}

export function WebsiteBuilderCanvas({
  pageId: pageIdProp,
  onPageIdChange,
  isDark: isDarkProp = false,
  initialAiContent = null,
  showBackLink = true,
  backLinkTo = '/admin/website-pages',
  backLinkLabel = 'Pages',
  onEditorReady,
  compactChrome = false,
  fillContainer = false,
  externalProject = null,
  embeddedMode = false,
  visualSyncKey = 0,
  delegateSave = false,
}: WebsiteBuilderCanvasProps): React.ReactElement {
  const { id: routeId } = useParams<{ id: string }>();
  const id = pageIdProp ?? routeId;
  const navigate = useNavigate();
  const isDark = isDarkProp;
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<GrapesEditor>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editorReady, setEditorReady] = useState<boolean>(false);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const [showCodeModal, setShowCodeModal] = useState<boolean>(false);
  const [codeContent, setCodeContent] = useState<CodeContent>({ html: '', css: '' });
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [importContent, setImportContent] = useState<CodeContent>({ html: '', css: '' });
  const [pageTitle, setPageTitle] = useState<string>('');
  const [pageSlug, setPageSlug] = useState<string>('');
  const [pageStatus, setPageStatus] = useState<string>('draft');
  const [activeDevice, setActiveDevice] = useState<string>('desktop');
  const [activePanel, setActivePanel] = useState<string>('blocks');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const initEditor = useCallback(async (): Promise<void> => {
    if (editorInstanceRef.current || !editorContainerRef.current) {
      return;
    }

    try {
      // Import GrapesJS and plugins
      const [
        grapesjs,
        gjsPresetWebpage,
        gjsBlocksBasic,
        gjsPluginForms,
        // gjsCustomCode, // Disabled
        gjsTabs,
        gjsTooltip,
        gjsTyped,
        gjsTouch,
        gjsParserPostcss,
        gjsStyleBg,
        gjsNavbar,
        gjsCountdown,
        gjsExport,
      ] = await Promise.all([
        import('grapesjs'),
        import('grapesjs-preset-webpage'),
        import('grapesjs-blocks-basic'),
        import('grapesjs-plugin-forms'),
        // import('grapesjs-custom-code'), // Disabled - creates rogue code panels
        import('grapesjs-tabs'),
        import('grapesjs-tooltip'),
        import('grapesjs-typed'),
        import('grapesjs-touch'),
        import('grapesjs-parser-postcss'),
        import('grapesjs-style-bg'),
        import('grapesjs-navbar'),
        import('grapesjs-component-countdown'),
        import('grapesjs-plugin-export'),
      ]);

      const GrapesJS = grapesjs.default || grapesjs;

      // Create the editor with full configuration
      const editor = GrapesJS.init({
        container: editorContainerRef.current,
        height: '100%',
        width: 'auto',
        fromElement: false,

        // Storage - disable default, we handle it ourselves
        storageManager: false,

        // Asset manager for images
        assetManager: {
          embedAsBase64: true,
          upload: false, // We'll handle uploads separately
          assets: [],
        },

        // Canvas configuration — Tailwind v4 browser must load as a script inside the iframe.
        canvas: {
          styles: CANVAS_BASE_STYLES,
          scripts: [TAILWIND_CANVAS_SCRIPT],
        },

        // Device manager
        deviceManager: {
          devices: [
            { name: 'Desktop', width: '' },
            { name: 'Tablet', width: '768px', widthMedia: '992px' },
            { name: 'Mobile portrait', width: '320px', widthMedia: '480px' },
          ]
        },

        // Selector manager
        selectorManager: {
          appendTo: '.selectors-container',
        },

        // Style manager - appendTo only, sectors defined in preset-webpage plugin options
        styleManager: {
          appendTo: '.styles-container',
        },

        // Block manager
        blockManager: {
          appendTo: '.blocks-container',
        },

        // Layer manager
        layerManager: {
          appendTo: '.layers-container',
        },

        // Trait manager - element attributes (href, src, alt, etc.)
        traitManager: {
          appendTo: '.traits-container',
        },

        // Panels - we handle these ourselves
        panels: {
          defaults: []
        },

        codeManager: {
          // grapesjs 0.21 accepts `appendTo: null` at runtime to disable the code manager
          // panel, but the shipped CodeManagerConfig types omit this option.
          // @ts-expect-error - appendTo is supported at runtime despite missing from types
          appendTo: null,
        },

        // Plugins
        plugins: [
          gjsPresetWebpage.default || gjsPresetWebpage,
          gjsBlocksBasic.default || gjsBlocksBasic,
          gjsPluginForms.default || gjsPluginForms,
          // gjsCustomCode.default || gjsCustomCode, // Disabled
          gjsTabs.default || gjsTabs,
          gjsTooltip.default || gjsTooltip,
          gjsTyped.default || gjsTyped,
          gjsTouch.default || gjsTouch,
          gjsParserPostcss.default || gjsParserPostcss,
          gjsStyleBg.default || gjsStyleBg,
          gjsNavbar.default || gjsNavbar,
          gjsCountdown.default || gjsCountdown,
          gjsExport.default || gjsExport,
        ],
        pluginsOpts: {
          [String(gjsPresetWebpage.default || gjsPresetWebpage)]: {
            blocksBasicOpts: false, // We're using gjsBlocksBasic separately
            blocks: ['link-block', 'quote', 'text-basic'],
            modalImportTitle: 'Import',
            modalImportButton: 'Import',
            modalImportContent: '',
            // Pass our custom style manager through the preset
            customStyleManager: [
              {
                name: 'General',
                open: true,
                buildProps: ['float', 'display', 'position', 'top', 'right', 'left', 'bottom'],
              },
              {
                name: 'Flex',
                open: false,
                buildProps: ['flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'align-content', 'order', 'flex-basis', 'flex-grow', 'flex-shrink', 'align-self'],
              },
              {
                name: 'Dimension',
                open: false,
                buildProps: ['width', 'height', 'max-width', 'min-height', 'margin', 'padding'],
              },
              {
                name: 'Typography',
                open: false,
                buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'line-height', 'text-align', 'text-decoration', 'text-shadow'],
                properties: [
                  { property: 'font-family' },
                  { property: 'font-size' },
                  { property: 'font-weight' },
                  { property: 'letter-spacing' },
                  { property: 'line-height' },
                  { property: 'text-align' },
                  { property: 'text-decoration' },
                  { property: 'text-shadow' },
                  {
                    property: 'color',
                    type: 'color',
                  },
                ],
              },
              {
                name: 'Decorations',
                open: false,
                buildProps: ['opacity', 'border-radius', 'border', 'box-shadow'],
                properties: [
                  { property: 'opacity' },
                  { property: 'border-radius' },
                  { property: 'border' },
                  { property: 'box-shadow' },
                  {
                    property: 'background-color',
                    type: 'color',
                  },
                  { property: 'background' },
                ],
              },
              {
                name: 'Extra',
                open: false,
                buildProps: ['transition', 'perspective', 'transform'],
              },
            ],
          },
          [String(gjsBlocksBasic.default || gjsBlocksBasic)]: {
            blocks: ['column1', 'column2', 'column3', 'column3-7', 'text', 'link', 'image', 'video', 'map'],
            flexGrid: true,
          },
          [String(gjsPluginForms.default || gjsPluginForms)]: {
            blocks: ['form', 'input', 'textarea', 'select', 'button', 'label', 'checkbox', 'radio'],
          },
          [String(gjsTabs.default || gjsTabs)]: {
            tabsBlock: { category: 'Extra' },
          },
          [String(gjsTyped.default || gjsTyped)]: {
            block: {
              category: 'Extra',
              content: {
                type: 'typed',
                'type-speed': 40,
                strings: ['Text row one', 'Text row two', 'Text row three'],
              }
            }
          },
          [String(gjsCountdown.default || gjsCountdown)]: {
            block: { category: 'Extra' },
          },
          [String(gjsExport.default || gjsExport)]: {
            btnLabel: 'Export ZIP',
          },
        },
      });

      // Store editor reference
      editorInstanceRef.current = editor;

      // Add custom blocks
      const bm = editor.BlockManager;

      // Hero section
      bm.add('hero-section', {
        label: 'Hero Section',
        category: 'Sections',
        content: `
          <section class="relative py-20 bg-gradient-to-r from-blue-600 to-purple-600">
            <div class="container mx-auto px-4 text-center">
              <h1 class="text-4xl md:text-6xl font-bold text-white mb-6">Welcome to Our Site</h1>
              <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Create amazing experiences.</p>
              <a href="#" class="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg">Get Started</a>
            </div>
          </section>
        `,
        attributes: { class: 'gjs-block-section' }
      });

      // Features grid
      bm.add('features-grid', {
        label: 'Features Grid',
        category: 'Sections',
        content: `
          <section class="py-16 bg-gray-50">
            <div class="container mx-auto px-4">
              <h2 class="text-3xl font-bold text-center mb-12">Our Features</h2>
              <div class="grid md:grid-cols-3 gap-8">
                <div class="text-center p-6 bg-white rounded-xl shadow-sm">
                  <div class="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span class="text-2xl">🚀</span>
                  </div>
                  <h3 class="text-xl font-semibold mb-2">Feature One</h3>
                  <p class="text-gray-600">Description of your amazing feature.</p>
                </div>
                <div class="text-center p-6 bg-white rounded-xl shadow-sm">
                  <div class="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span class="text-2xl">⚡</span>
                  </div>
                  <h3 class="text-xl font-semibold mb-2">Feature Two</h3>
                  <p class="text-gray-600">Description of your amazing feature.</p>
                </div>
                <div class="text-center p-6 bg-white rounded-xl shadow-sm">
                  <div class="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span class="text-2xl">🎯</span>
                  </div>
                  <h3 class="text-xl font-semibold mb-2">Feature Three</h3>
                  <p class="text-gray-600">Description of your amazing feature.</p>
                </div>
              </div>
            </div>
          </section>
        `,
      });

      // Pricing table
      bm.add('pricing-table', {
        label: 'Pricing Table',
        category: 'Sections',
        content: `
          <section class="py-16 bg-white">
            <div class="container mx-auto px-4">
              <h2 class="text-3xl font-bold text-center mb-4">Simple Pricing</h2>
              <p class="text-gray-600 text-center mb-12">Choose the plan that works for you</p>
              <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div class="border border-gray-200 rounded-2xl p-8">
                  <h3 class="text-xl font-semibold mb-2">Starter</h3>
                  <p class="text-gray-500 mb-4">For individuals</p>
                  <div class="mb-6"><span class="text-4xl font-bold">$9</span><span class="text-gray-500">/mo</span></div>
                  <ul class="space-y-3 mb-8 text-gray-600">
                    <li>✓ 5 Projects</li>
                    <li>✓ Basic Support</li>
                    <li>✓ 1GB Storage</li>
                  </ul>
                  <a href="#" class="block text-center py-3 border border-gray-300 rounded-lg hover:bg-gray-50">Get Started</a>
                </div>
                <div class="border-2 border-blue-600 rounded-2xl p-8 relative">
                  <div class="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-sm px-3 py-1 rounded-full">Popular</div>
                  <h3 class="text-xl font-semibold mb-2">Professional</h3>
                  <p class="text-gray-500 mb-4">For teams</p>
                  <div class="mb-6"><span class="text-4xl font-bold">$29</span><span class="text-gray-500">/mo</span></div>
                  <ul class="space-y-3 mb-8 text-gray-600">
                    <li>✓ Unlimited Projects</li>
                    <li>✓ Priority Support</li>
                    <li>✓ 10GB Storage</li>
                    <li>✓ Team Collaboration</li>
                  </ul>
                  <a href="#" class="block text-center py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Get Started</a>
                </div>
                <div class="border border-gray-200 rounded-2xl p-8">
                  <h3 class="text-xl font-semibold mb-2">Enterprise</h3>
                  <p class="text-gray-500 mb-4">For organizations</p>
                  <div class="mb-6"><span class="text-4xl font-bold">$99</span><span class="text-gray-500">/mo</span></div>
                  <ul class="space-y-3 mb-8 text-gray-600">
                    <li>✓ Everything in Pro</li>
                    <li>✓ Dedicated Support</li>
                    <li>✓ Unlimited Storage</li>
                    <li>✓ Custom Integrations</li>
                  </ul>
                  <a href="#" class="block text-center py-3 border border-gray-300 rounded-lg hover:bg-gray-50">Contact Sales</a>
                </div>
              </div>
            </div>
          </section>
        `,
      });

      // Testimonials
      bm.add('testimonials', {
        label: 'Testimonials',
        category: 'Sections',
        content: `
          <section class="py-16 bg-gray-50">
            <div class="container mx-auto px-4">
              <h2 class="text-3xl font-bold text-center mb-12">What Our Customers Say</h2>
              <div class="grid md:grid-cols-3 gap-8">
                <div class="bg-white p-6 rounded-xl shadow-sm">
                  <div class="flex items-center gap-1 text-yellow-400 mb-4">★★★★★</div>
                  <p class="text-gray-600 mb-4">"Amazing product! It has completely transformed how we work."</p>
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-gray-300 rounded-full"></div>
                    <div>
                      <p class="font-semibold">John Doe</p>
                      <p class="text-sm text-gray-500">CEO, Company</p>
                    </div>
                  </div>
                </div>
                <div class="bg-white p-6 rounded-xl shadow-sm">
                  <div class="flex items-center gap-1 text-yellow-400 mb-4">★★★★★</div>
                  <p class="text-gray-600 mb-4">"The best investment we've made this year. Highly recommended!"</p>
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-gray-300 rounded-full"></div>
                    <div>
                      <p class="font-semibold">Jane Smith</p>
                      <p class="text-sm text-gray-500">Designer</p>
                    </div>
                  </div>
                </div>
                <div class="bg-white p-6 rounded-xl shadow-sm">
                  <div class="flex items-center gap-1 text-yellow-400 mb-4">★★★★★</div>
                  <p class="text-gray-600 mb-4">"Outstanding support and an incredible product. Love it!"</p>
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-gray-300 rounded-full"></div>
                    <div>
                      <p class="font-semibold">Mike Johnson</p>
                      <p class="text-sm text-gray-500">Developer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        `,
      });

      // CTA section
      bm.add('cta-section', {
        label: 'Call to Action',
        category: 'Sections',
        content: `
          <section class="py-16 bg-blue-600">
            <div class="container mx-auto px-4 text-center">
              <h2 class="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
              <p class="text-xl text-white/90 mb-8">Join thousands of satisfied customers today.</p>
              <a href="#" class="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100">Sign Up Now</a>
            </div>
          </section>
        `,
      });

      // Footer
      bm.add('footer', {
        label: 'Footer',
        category: 'Sections',
        content: `
          <footer class="bg-gray-900 text-white py-12">
            <div class="container mx-auto px-4">
              <div class="grid md:grid-cols-4 gap-8">
                <div>
                  <h4 class="text-lg font-semibold mb-4">Company</h4>
                  <p class="text-gray-400">Building amazing products since 2024.</p>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-4">Product</h4>
                  <ul class="space-y-2 text-gray-400">
                    <li><a href="#" class="hover:text-white">Features</a></li>
                    <li><a href="#" class="hover:text-white">Pricing</a></li>
                  </ul>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-4">Support</h4>
                  <ul class="space-y-2 text-gray-400">
                    <li><a href="#" class="hover:text-white">Help</a></li>
                    <li><a href="#" class="hover:text-white">Contact</a></li>
                  </ul>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-4">Legal</h4>
                  <ul class="space-y-2 text-gray-400">
                    <li><a href="#" class="hover:text-white">Privacy</a></li>
                    <li><a href="#" class="hover:text-white">Terms</a></li>
                  </ul>
                </div>
              </div>
              <div class="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
                <p>&copy; 2024 Company. All rights reserved.</p>
              </div>
            </div>
          </footer>
        `,
      });

      // Add device commands
      editor.Commands.add('set-device-desktop', {
        run: (ed: Editor) => ed.setDevice('Desktop')
      });
      editor.Commands.add('set-device-tablet', {
        run: (ed: Editor) => ed.setDevice('Tablet')
      });
      editor.Commands.add('set-device-mobile', {
        run: (ed: Editor) => ed.setDevice('Mobile portrait')
      });

      // Track changes for autosave
      editor.on('change:changesCount', () => {
        setHasUnsavedChanges(true);
      });

      editor.on('canvas:frame:load:head', ({ window: frameWindow }: { window: Window }) => {
        const doc = frameWindow.document;
        if (!doc.querySelector('[data-futuredev-tailwind]')) {
          const script = doc.createElement('script');
          script.src = TAILWIND_CANVAS_SCRIPT;
          script.dataset.futuredevTailwind = 'true';
          doc.head.appendChild(script);
        }
      });

      const applyAiContent = (aiSite: AiGeneratedSite): boolean => {
        if (!aiSite.html?.trim()) return false;
        const normalized = normalizeAiWebsiteHtml(aiSite.html, aiSite.css || '');
        applyContentToEditor(editor, {
          components: normalized.html,
          css: normalized.css,
          styles: [],
        });
        if (aiSite.siteName) {
          setPageTitle(aiSite.siteName);
          setPageSlug(aiSite.siteName.toLowerCase().replace(/\s+/g, '-'));
        }
        return true;
      };

      if (embeddedMode && externalProject) {
        applyContentToEditor(editor, {
          components: externalProject.html,
          css: externalProject.css,
          styles: [],
        });
      } else if (id && id !== 'new') {
        await loadPage(id);
      } else {
        const aiContent = initialAiContent
          || (() => {
            const stored = sessionStorage.getItem('ai_generated_site');
            return stored ? JSON.parse(stored) as AiGeneratedSite : null;
          })();
        if (aiContent && applyAiContent(aiContent)) {
          sessionStorage.removeItem('ai_generated_site');
          setSuccess('AI-generated site loaded! Start editing to customize.');
          setTimeout(() => setSuccess(null), 4000);
        } else {
          editor.setComponents(PAGE_TEMPLATES[0].content);
        }
      }

      const sessionAiContent = initialAiContent
        || (() => {
          const stored = sessionStorage.getItem('ai_generated_site');
          return stored ? JSON.parse(stored) as AiGeneratedSite : null;
        })();
      if (id && id !== 'new' && sessionAiContent && applyAiContent(sessionAiContent)) {
        sessionStorage.removeItem('ai_generated_site');
        setSuccess('AI-generated site loaded! Start editing to customize.');
        setTimeout(() => setSuccess(null), 4000);
      }

      if (onEditorReady) {
        onEditorReady({
          getHtml: () => String(editor.getHtml()),
          getCss: () => String(editor.getCss()),
          setContent: (html, css) => {
            const normalized = normalizeAiWebsiteHtml(html, css || '');
            applyContentToEditor(editor, {
              components: normalized.html,
              css: normalized.css,
              styles: [],
            });
            setHasUnsavedChanges(true);
          },
        });
      }

      setEditorReady(true);
      setLoading(false);

    } catch (err: unknown) {
      console.error('Init editor failed:', err);
      setError(`Failed to load editor: ${extractApiError(err, 'Unknown error')}`);
      setLoading(false);
    }
  }, [id, initialAiContent, onEditorReady, embeddedMode, externalProject]);

  useEffect(() => {
    if (!editorReady || !embeddedMode || !externalProject || !editorInstanceRef.current) return;
    applyContentToEditor(editorInstanceRef.current, {
      components: externalProject.html,
      css: externalProject.css,
      styles: [],
    });
  }, [visualSyncKey, editorReady, embeddedMode, externalProject]);

  // Initialize editor when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      initEditor();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
    };
  }, [initEditor]);

  // Autosave every 30 seconds if there are changes
  useEffect(() => {
    if (!editorReady || !hasUnsavedChanges || delegateSave) return;

    const autosaveTimer = setTimeout(() => {
      const { handleSave: save, pageTitle: title, pageSlug: slug } = latestRef.current;
      if (title && slug) {
        void save(true);
      }
    }, 30000);

    return () => clearTimeout(autosaveTimer);
  }, [hasUnsavedChanges, editorReady, delegateSave]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      const handlers = latestRef.current;

      if (e.key === 's') {
        e.preventDefault();
        void handlers.handleSave(false);
      } else if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handlers.handleUndo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        handlers.handleRedo();
      } else if (e.key === 'e') {
        e.preventDefault();
        handlers.handlePreview();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadPage = async (pageId: string): Promise<void> => {
    try {
      const response = await api.get(`/websites/pages/${pageId}`);
      const pageData: PageData = response.data.data;

      setPageTitle(pageData.title || '');
      setPageSlug(pageData.slug || '');
      setPageStatus(pageData.status || 'draft');

      if (editorInstanceRef.current && pageData.content) {
        try {
          applyContentToEditor(editorInstanceRef.current, pageData.content);
        } catch (e: unknown) {
          console.error('Parse content failed:', e);
        }
      }
    } catch (err: unknown) {
      console.error('Load page failed:', err);
      setError(extractApiError(err, 'Failed to load page'));
    }
  };

  const handleSave = async (silent: boolean = false): Promise<void> => {
    if (!editorInstanceRef.current) return;

    try {
      setSaving(true);
      const editor = editorInstanceRef.current;

      const content = {
        components: editor.getComponents(),
        styles: editor.getStyle(),
        css: editor.getCss(),
        html: editor.getHtml(),
      };

      const pageData = {
        title: pageTitle || 'Untitled Page',
        slug: pageSlug || 'untitled-page',
        content: content,
        status: pageStatus,
      };

      let response;
      if (id && id !== 'new') {
        response = await api.put(`/websites/pages/${id}`, pageData);
      } else {
        response = await api.post('/websites/pages', pageData);
        const newId = response.data.data.id;
        if (onPageIdChange) {
          onPageIdChange(String(newId));
        } else {
          navigate(`/admin/website-builder/${newId}`, { replace: true });
        }
      }

      setError(null);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());

      if (!silent) {
        setSuccess('Page saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: unknown) {
      console.error('Save page failed:', err);
      setError(extractApiError(err, 'Failed to save page'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || id === 'new' || !confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      await api.delete(`/websites/pages/${id}`);
      navigate('/admin/website-pages');
    } catch (err: unknown) {
      console.error('Delete page failed:', err);
      setError('Failed to delete page');
    }
  };

  const handleDeviceChange = (device: string): void => {
    setActiveDevice(device);
    if (editorInstanceRef.current) {
      editorInstanceRef.current.runCommand(`set-device-${device}`);
    }
  };

  const handleUndo = (): void => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.UndoManager.undo();
    }
  };

  const handleRedo = (): void => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.UndoManager.redo();
    }
  };

  const handleViewCode = (): void => {
    if (editorInstanceRef.current) {
      setCodeContent({
        html: editorInstanceRef.current.getHtml(),
        css: editorInstanceRef.current.getCss(),
      });
      setShowCodeModal(true);
    }
  };

  const handlePreview = (): void => {
    if (editorInstanceRef.current) {
      const html = editorInstanceRef.current.getHtml();
      const css = editorInstanceRef.current.getCss();
      const previewWindow = window.open('', '_blank');
      if (!previewWindow) return;
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${pageTitle || 'Preview'}</title>
          <script src="${TAILWIND_CANVAS_SCRIPT}"></script>
          <style>${css}</style>
        </head>
        <body>${html}</body>
        </html>
      `);
      previewWindow.document.close();
    }
  };

  // The autosave timer and the shortcut listener must not be rebuilt on every
  // render, but they still have to call the current handlers rather than the ones
  // captured when the effect last ran. Hand-listing the state each handler reads
  // (as the dependency arrays previously did) drifts out of date silently.
  const latestRef = useRef({ handleSave, handleUndo, handleRedo, handlePreview, pageTitle, pageSlug });
  latestRef.current = { handleSave, handleUndo, handleRedo, handlePreview, pageTitle, pageSlug };

  const handleExport = (): void => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.runCommand('gjs-export-zip');
    }
  };

  const applyTemplate = (template: PageTemplate): void => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setComponents(template.content);
      setShowTemplateModal(false);
      setHasUnsavedChanges(true);
    }
  };

  const handleClear = (): void => {
    if (confirm('Are you sure you want to clear the canvas? This cannot be undone.')) {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.setComponents('');
        editorInstanceRef.current.setStyle('');
        setHasUnsavedChanges(true);
      }
    }
  };

  return (
    <div className={`${fillContainer ? 'h-full min-h-0' : 'h-screen'} flex flex-col overflow-hidden ${isDark ? 'bg-[#0a0c10]' : 'bg-gray-100'}`}>
      {/* Header Toolbar */}
      <div className={`flex shrink-0 items-center justify-between gap-2 overflow-x-auto border-b px-4 py-2 backdrop-blur-sm ${isDark ? 'bg-slate-900/90 border-slate-700/50' : 'bg-white/95 border-gray-200'}`}>
        <div className="flex items-center gap-3">
          {showBackLink && (
            <Link
              to={backLinkTo}
              className={`text-sm font-medium ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              ← {backLinkLabel}
            </Link>
          )}
          <div className={`h-6 w-px ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={pageTitle}
              onChange={(e) => { setPageTitle(e.target.value); setHasUnsavedChanges(true); }}
              placeholder="Page Title"
              className={`px-3 py-1.5 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40 ${isDark ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'border-gray-300 bg-white text-gray-900'}`}
            />
            <input
              type="text"
              value={pageSlug}
              onChange={(e) => { setPageSlug(e.target.value); setHasUnsavedChanges(true); }}
              placeholder="page-slug"
              className={`px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent w-32 ${isDark ? 'bg-slate-800 border-slate-600 text-slate-300 placeholder-slate-500' : 'border-gray-300 text-gray-600'}`}
            />
          </div>
        </div>

        {/* Center Tools */}
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className={`flex items-center gap-1 rounded-lg p-1 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
            <button onClick={handleUndo} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Undo">
              <ArrowUturnLeftIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
            </button>
            <button onClick={handleRedo} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Redo">
              <ArrowUturnRightIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
            </button>
          </div>

          {/* Device Switcher */}
          <div className={`flex items-center gap-1 rounded-lg p-1 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
            <button
              onClick={() => handleDeviceChange('desktop')}
              className={`p-1.5 rounded ${activeDevice === 'desktop' ? (isDark ? 'bg-slate-700 shadow text-primary-400' : 'bg-white shadow text-primary-600') : (isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700')}`}
              title="Desktop"
            >
              <ComputerDesktopIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeviceChange('tablet')}
              className={`p-1.5 rounded ${activeDevice === 'tablet' ? (isDark ? 'bg-slate-700 shadow text-primary-400' : 'bg-white shadow text-primary-600') : (isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700')}`}
              title="Tablet"
            >
              <DeviceTabletIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeviceChange('mobile')}
              className={`p-1.5 rounded ${activeDevice === 'mobile' ? (isDark ? 'bg-slate-700 shadow text-primary-400' : 'bg-white shadow text-primary-600') : (isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700')}`}
              title="Mobile"
            >
              <DevicePhoneMobileIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className={`flex items-center gap-1 rounded-lg p-1 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
            {!compactChrome && (
              <>
                <button onClick={() => setShowTemplateModal(true)} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Templates">
                  <DocumentDuplicateIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
                </button>
                <button onClick={() => setShowImportModal(true)} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Import HTML/CSS">
                  <CloudArrowUpIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
                </button>
              </>
            )}
            <button onClick={handleViewCode} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="View Code">
              <CodeBracketIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
            </button>
            <button onClick={handlePreview} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Preview">
              <EyeIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
            </button>
            {!compactChrome && (
              <button onClick={handleExport} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Export">
                <ArrowDownTrayIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
              </button>
            )}
            <button onClick={handleClear} className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`} title="Clear Canvas">
              <TrashIcon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-2">
          {lastSaved && (
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-500 font-medium">Unsaved changes</span>
          )}
          <select
            value={pageStatus}
            onChange={(e) => { setPageStatus(e.target.value); setHasUnsavedChanges(true); }}
            className={`px-2 py-1.5 border rounded-lg text-sm ${isDark ? 'bg-slate-800 border-slate-600 text-slate-300' : 'border-gray-300'}`}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          {!delegateSave && (
            <button
              onClick={() => handleSave(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              disabled={saving}
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}

          {id && id !== 'new' && (
            <button
              onClick={handleDelete}
              className={`p-1.5 text-red-500 rounded-lg transition-colors ${isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-50'}`}
              title="Delete Page"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className={`flex w-64 shrink-0 flex-col border-r ${isDark ? 'bg-slate-900/80 border-slate-700/50' : 'bg-white border-gray-200'}`}>
          {/* Panel Tabs */}
          <div className={`flex border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
            <button
              onClick={() => setActivePanel('blocks')}
              className={`flex-1 py-2 text-xs font-medium ${activePanel === 'blocks' ? 'text-primary-400 border-b-2 border-primary-400' : (isDark ? 'text-slate-400' : 'text-gray-500')}`}
              title="Drag & drop components"
            >
              <Squares2X2Icon className="w-4 h-4 mx-auto mb-0.5" />
              Blocks
            </button>
            <button
              onClick={() => setActivePanel('layers')}
              className={`flex-1 py-2 text-xs font-medium ${activePanel === 'layers' ? 'text-primary-400 border-b-2 border-primary-400' : (isDark ? 'text-slate-400' : 'text-gray-500')}`}
              title="Component layer tree"
            >
              <SwatchIcon className="w-4 h-4 mx-auto mb-0.5" />
              Layers
            </button>
            {compactChrome && (
              <button
                onClick={() => setActivePanel('styles')}
                className={`flex-1 py-2 text-xs font-medium ${activePanel === 'styles' ? 'text-primary-400 border-b-2 border-primary-400' : (isDark ? 'text-slate-400' : 'text-gray-500')}`}
                title="Element styles"
              >
                <PaintBrushIcon className="w-4 h-4 mx-auto mb-0.5" />
                Styles
              </button>
            )}
            <button
              onClick={() => setActivePanel('settings')}
              className={`flex-1 py-2 text-xs font-medium ${activePanel === 'settings' ? 'text-primary-400 border-b-2 border-primary-400' : (isDark ? 'text-slate-400' : 'text-gray-500')}`}
              title="Element attributes & settings"
            >
              <Cog6ToothIcon className="w-4 h-4 mx-auto mb-0.5" />
              Settings
            </button>
          </div>

          {/* Panel Content */}
          <div className={`min-h-0 flex-1 overflow-y-auto ${activePanel === 'blocks' ? '' : 'hidden'}`}>
            <div className="blocks-container"></div>
          </div>
          <div className={`min-h-0 flex-1 overflow-y-auto ${activePanel === 'layers' ? '' : 'hidden'}`}>
            <div className="layers-container"></div>
          </div>
          {compactChrome && (
            <div className={`flex min-h-0 flex-1 flex-col overflow-hidden ${activePanel === 'styles' ? '' : 'hidden'}`}>
              <div className={`selectors-container shrink-0 border-b p-2 ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}></div>
              <div className="styles-container min-h-0 flex-1 overflow-y-auto"></div>
            </div>
          )}
          <div className={`min-h-0 flex-1 overflow-y-auto ${activePanel === 'settings' ? '' : 'hidden'}`}>
            <div className="traits-container"></div>
          </div>
        </div>

        {/* Canvas */}
        <div className={`relative min-h-0 min-w-0 flex-1 ${isDark ? 'bg-slate-800/50' : 'bg-gray-200'}`}>
          {loading && (
            <div className={`absolute inset-0 flex items-center justify-center z-10 ${isDark ? 'bg-slate-900/80' : 'bg-white/80'}`}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto"></div>
                <p className={`mt-3 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Loading editor...</p>
              </div>
            </div>
          )}
          <div ref={editorContainerRef} className="h-full w-full" />
        </div>

        {/* Right Sidebar - Style Manager (admin layout only) */}
        {!compactChrome && (
          <div className={`flex w-72 shrink-0 flex-col overflow-hidden border-l ${isDark ? 'bg-slate-900/80 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className={`shrink-0 border-b p-3 ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Style Manager</h3>
            </div>
            <div className={`selectors-container shrink-0 border-b p-2 ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}></div>
            <div className="styles-container min-h-0 flex-1 overflow-y-auto"></div>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className={`rounded-xl shadow-2xl p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Choose a Template</h2>
              <button onClick={() => setShowTemplateModal(false)} className={`${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}>
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {PAGE_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => applyTemplate(template)}
                  className={`border rounded-lg p-4 transition-colors text-center ${isDark ? 'border-slate-700 hover:border-primary-400 hover:bg-primary-900/20' : 'border-gray-200 hover:border-primary-500 hover:bg-primary-50'}`}
                >
                  <div className="text-4xl mb-2">{template.thumbnail}</div>
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{template.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Code Modal */}
      {showCodeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className={`rounded-xl shadow-2xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white'} overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Page Code</h2>
              <button onClick={() => setShowCodeModal(false)} className={`${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}>
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>HTML</h3>
                  <button
                    onClick={() => { navigator.clipboard.writeText(codeContent.html); setCopiedCode('html'); setTimeout(() => setCopiedCode(null), 2000); }}
                    className={`text-xs px-2 py-1 rounded transition-colors ${copiedCode === 'html' ? 'btn-fill-green' : (isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')}`}
                  >
                    {copiedCode === 'html' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                  <code>{codeContent.html}</code>
                </pre>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>CSS</h3>
                  <button
                    onClick={() => { navigator.clipboard.writeText(codeContent.css); setCopiedCode('css'); setTimeout(() => setCopiedCode(null), 2000); }}
                    className={`text-xs px-2 py-1 rounded transition-colors ${copiedCode === 'css' ? 'btn-fill-green' : (isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')}`}
                  >
                    {copiedCode === 'css' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                  <code>{codeContent.css}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import HTML/CSS Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className={`rounded-xl shadow-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white'} overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Import HTML/CSS</h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Paste your HTML code below. Tailwind CSS classes are fully supported.
                </p>
              </div>
              <button onClick={() => setShowImportModal(false)} className={`${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}>
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4">
              {/* HTML Input */}
              <div>
                <label htmlFor="website-builder-import-html" className={`block text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  HTML Code <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="website-builder-import-html"
                  value={importContent.html}
                  onChange={(e) => setImportContent(prev => ({ ...prev, html: e.target.value }))}
                  className="w-full h-64 p-4 font-mono text-sm bg-gray-900 text-gray-100 rounded-lg border-0 focus:ring-2 focus:ring-blue-500"
                  placeholder={`<div class="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700">
  <div class="container mx-auto px-6 py-20">
    <h1 class="text-5xl font-bold text-white mb-6">
      Hello World
    </h1>
    <p class="text-xl text-white/90">
      This is a sample component with Tailwind CSS classes.
    </p>
    <button class="mt-8 px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100">
      Get Started
    </button>
  </div>
</div>`}
                />
              </div>

              {/* CSS Input (Optional) */}
              <div>
                <label htmlFor="website-builder-import-css" className={`block text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Custom CSS <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="website-builder-import-css"
                  value={importContent.css}
                  onChange={(e) => setImportContent(prev => ({ ...prev, css: e.target.value }))}
                  className="w-full h-32 p-4 font-mono text-sm bg-gray-900 text-gray-100 rounded-lg border-0 focus:ring-2 focus:ring-blue-500"
                  placeholder={`.custom-class {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}`}
                />
              </div>

              {/* Import Options */}
              <div className={`border rounded-lg p-4 ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>💡 Tips</h4>
                <ul className={`text-sm space-y-1 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                  <li>• Tailwind CSS classes are automatically supported</li>
                  <li>• You can import entire page sections or individual components</li>
                  <li>• Custom CSS will be added to the page styles</li>
                  <li>• Use semantic HTML for better editing experience</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setImportContent({ html: '', css: '' });
                }}
                className={`px-4 py-2 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-800'}`}
              >
                Clear
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className={`px-4 py-2 border rounded-lg ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!importContent.html.trim()) {
                      setError('Please enter HTML code to import');
                      return;
                    }

                    const editor = editorInstanceRef.current;
                    if (!editor) {
                      setError('Editor not initialized');
                      return;
                    }

                    try {
                      const normalized = normalizeAiWebsiteHtml(importContent.html, importContent.css);
                      const mergedCss = [editor.getCss(), normalized.css].filter(Boolean).join('\n');
                      applyContentToEditor(editor, {
                        components: normalized.html,
                        css: mergedCss,
                        styles: [],
                      });

                      setShowImportModal(false);
                      setImportContent({ html: '', css: '' });
                      setHasUnsavedChanges(true);
                      setSuccess('Content imported successfully!');
                      setTimeout(() => setSuccess(null), 3000);
                    } catch (err: unknown) {
                      console.error('Import failed:', err);
                      setError('Failed to import content: ' + (extractApiError(err, 'Unknown error')));
                    }
                  }}
                  disabled={!importContent.html.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <CloudArrowUpIcon className="w-4 h-4" />
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {success && (
        <div className={`fixed bottom-4 right-4 border rounded-lg p-4 max-w-md shadow-lg ${isDark ? 'bg-green-900/50 border-green-700' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-green-800' : 'bg-green-100'}`}>
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-800'}`}>{success}</p>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className={`fixed bottom-4 right-4 border rounded-lg p-4 max-w-md shadow-lg ${isDark ? 'bg-red-900/50 border-red-700' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-3">
            <XMarkIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className={`font-semibold ${isDark ? 'text-red-300' : 'text-red-800'}`}>Error</h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-700'}`}>{error}</p>
            </div>
            <button onClick={() => setError(null)} className={`${isDark ? 'text-red-400 hover:text-red-200' : 'text-red-600 hover:text-red-800'}`}>
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* GrapesJS Styles - Dual mode dark/light */}
      <style>{`
        /* === SHARED STYLES (both themes) === */
        .gjs-block {
          width: calc(50% - 5px);
          min-height: 55px;
          margin: 2px;
          padding: 6px;
          border-radius: 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: grab;
          transition: all 0.15s;
          font-size: 10px;
          text-align: center;
        }
        .gjs-block-label { font-weight: 500; }
        .gjs-block svg { width: 24px; height: 24px; fill: currentColor; }
        .gjs-block-category { margin-bottom: 4px; }
        .gjs-block-category .gjs-title {
          padding: 8px 10px;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
        }
        .gjs-blocks-c { display: flex; flex-wrap: wrap; padding: 4px; }
        .gjs-layers { font-size: 12px; }
        .gjs-layer { padding: 5px 8px; }
        .gjs-layer-title { font-weight: 500; }
        .gjs-layer-name { font-size: 11px; }
        .gjs-sm-sector-title {
          padding: 8px 10px;
          font-weight: 600;
          font-size: 11px;
          cursor: pointer;
        }
        .gjs-sm-properties { padding: 8px 10px; }
        .gjs-sm-property { margin-bottom: 6px; }
        .gjs-sm-label { font-size: 10px; margin-bottom: 3px; }
        .gjs-field { border-radius: 4px; padding: 4px 6px; font-size: 11px; }
        .gjs-field-color-picker { border-radius: 4px; }
        .gjs-clm-tags { padding: 4px; }
        .gjs-clm-tag { padding: 3px 6px; font-size: 10px; border-radius: 4px; margin: 2px; }
        .gjs-clm-sels-info { font-size: 11px; padding: 8px; }
        .gjs-clm-header { padding: 8px; }
        .gjs-clm-label { font-size: 11px; }
        .gjs-sm-btn { padding: 4px 8px; font-size: 11px; }
        .gjs-clm-select, .gjs-sm-select { font-size: 11px; padding: 4px 8px; }

        /* SVG size constraints */
        .gjs-clm-tags #gjs-clm-add-tag,
        .gjs-clm-tags .gjs-clm-tag-close,
        .gjs-sm-stack #gjs-sm-add,
        .gjs-clm-sel-id #gjs-clm-checkbox,
        .gjs-clm-sels-info svg,
        .gjs-clm-tags svg,
        .gjs-sm-stack svg,
        .gjs-field svg,
        .selectors-container svg,
        .styles-container svg {
          width: 14px !important; height: 14px !important;
          max-width: 14px !important; max-height: 14px !important;
        }
        .gjs-sm-btn svg { width: 12px !important; height: 12px !important; }
        .gjs-clm-select svg, .gjs-sm-select svg, .gjs-sel-caret {
          width: 12px !important; height: 12px !important;
        }
        .gjs-one-bg svg, .gjs-two-color svg, .gjs-pn-btn svg {
          width: 16px !important; height: 16px !important;
        }

        /* Canvas layout */
        .gjs-cv-canvas {
          width: 100% !important; height: 100% !important;
          top: 0 !important; left: 0 !important;
          position: relative !important; overflow: auto !important;
        }
        .gjs-cv-canvas__frames {
          width: 100% !important; height: 100% !important;
          position: relative !important; padding: 20px !important;
          box-sizing: border-box !important;
        }
        .gjs-frame-wrapper {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border-radius: 4px; overflow: hidden;
        }
        .gjs-frame { width: 100% !important; height: 100% !important; border: none !important; }
        .gjs-editor { width: 100% !important; height: 100% !important; position: relative !important; }
        .gjs-editor-cont { width: 100% !important; height: 100% !important; overflow: hidden !important; }
        .gjs-pn-panels { display: none; }
        .gjs-cv-canvas__top-zone, .gjs-off-prv { display: none !important; }

        /* Rogue elements */
        .gjs-mdl-dialog, .gjs-mdl-container, .gjs-am-assets,
        .gjs-cm-editor, .gjs-export-dl,
        body > .gjs-rte-toolbar, body > .gjs-pn-commands,
        body > .gjs-off-prv, body > .gjs-mdl-container {
          position: fixed !important; z-index: 9999 !important;
        }

        /* Color picker */
        .gjs-field-color-picker { display: flex; align-items: center; gap: 8px; }
        .gjs-field-color-picker .gjs-checker-bg {
          width: 24px; height: 24px; border-radius: 4px; cursor: pointer;
        }
        .gjs-field-color-picker input { flex: 1; border-radius: 4px; padding: 6px 10px; font-size: 13px; }

        /* Spectrum color picker */
        .sp-container { z-index: 99999 !important; border-radius: 8px !important; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important; }
        .sp-container.sp-hidden { display: none !important; }

        /* Toolbar & badges */
        .gjs-rte-toolbar { border-radius: 6px; padding: 4px; }
        .gjs-rte-action { color: white; padding: 4px 8px; border-radius: 4px; }
        .gjs-rte-action:hover { background: #374151; }
        .gjs-toolbar { border-radius: 6px; }
        .gjs-toolbar-item { padding: 6px; color: white; }
        .gjs-selected { outline: 2px solid #3b82f6 !important; outline-offset: -2px; }
        .gjs-highlighter { outline: 2px dashed #3b82f6; }
        .gjs-badge { font-size: 10px; padding: 2px 6px; border-radius: 4px; }
        .gjs-trt-trait { padding: 6px 0; }
        .gjs-trt-trait .gjs-label { font-size: 10px; }

        /* === THEME-SPECIFIC STYLES === */
        ${isDark ? `
          /* DARK THEME */
          .gjs-block {
            border: 1px solid #334155;
            background: #1e293b;
            color: #cbd5e1;
          }
          .gjs-block:hover { border-color: #6366f1; background: #312e81; }
          .gjs-block-label { color: #e2e8f0; }
          .gjs-block-category .gjs-title {
            color: #94a3b8;
            background: #0f172a;
            border-bottom: 1px solid #1e293b;
          }
          .gjs-layer { border-bottom: 1px solid #1e293b; }
          .gjs-layer:hover { background: #1e293b; }
          .gjs-sm-sector { border-bottom: 1px solid #334155; }
          .gjs-sm-sector-title { color: #e2e8f0; background: #0f172a; }
          .gjs-sm-label { color: #94a3b8; }
          .gjs-field {
            background: #0f172a !important;
            border: 1px solid #334155 !important;
            color: #e2e8f0 !important;
          }
          .gjs-field:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2); }
          .gjs-field input, .gjs-field select { color: #e2e8f0 !important; background: transparent !important; }
          .gjs-one-bg { background-color: #0f172a !important; }
          .gjs-two-color { color: #e2e8f0 !important; }
          .gjs-three-bg { background-color: #1e293b !important; }
          .gjs-four-color, .gjs-four-color-h:hover { color: #818cf8 !important; }
          .selectors-container { background: #0f172a !important; }
          .selectors-container .gjs-clm-sels-info,
          .selectors-container .gjs-clm-header,
          .selectors-container .gjs-clm-tags { background: #0f172a !important; color: #e2e8f0 !important; }
          .selectors-container .gjs-clm-label,
          .selectors-container .gjs-sm-label { color: #94a3b8 !important; }
          .gjs-clm-tags .gjs-clm-tag { background: #334155 !important; color: #e2e8f0 !important; }
          .gjs-clm-tags .gjs-clm-tag.gjs-three-bg { background: #6366f1 !important; color: white !important; }
          .gjs-clm-select, .gjs-sm-select {
            background: #0f172a !important; color: #e2e8f0 !important; border-color: #334155 !important;
          }
          .gjs-cv-canvas { background: #1e293b; }
          .gjs-cv-canvas-bg { background: #1e293b !important; }
          .gjs-frame-wrapper { background: white; }
          .gjs-field-color-picker .gjs-checker-bg { border: 1px solid #475569; }
          .gjs-field-color-picker input { border: 1px solid #334155; color: #e2e8f0; background: #0f172a; }
          .sp-container { background: #1e293b !important; border: 1px solid #334155 !important; }
          .gjs-rte-toolbar { background: #0f172a; }
          .gjs-toolbar { background: #0f172a; }
          .gjs-badge { background: #6366f1; color: white; }
          .gjs-trt-trait .gjs-label { color: #94a3b8; }
        ` : `
          /* LIGHT THEME */
          .gjs-block {
            border: 1px solid #e5e7eb;
            background: white;
            color: #374151;
          }
          .gjs-block:hover { border-color: #3b82f6; background: #eff6ff; }
          .gjs-block-label { color: #374151; }
          .gjs-block-category .gjs-title {
            color: #6b7280;
            background: #f9fafb;
            border-bottom: 1px solid #e5e7eb;
          }
          .gjs-layer { border-bottom: 1px solid #f3f4f6; }
          .gjs-layer:hover { background: #f9fafb; }
          .gjs-sm-sector { border-bottom: 1px solid #e5e7eb; }
          .gjs-sm-sector-title { color: #374151; background: #f9fafb; }
          .gjs-sm-label { color: #6b7280; }
          .gjs-field {
            background: white !important;
            border: 1px solid #d1d5db !important;
            color: #374151 !important;
          }
          .gjs-field:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2); }
          .gjs-field input, .gjs-field select { color: #374151 !important; }
          .gjs-one-bg { background-color: #f9fafb !important; }
          .gjs-two-color { color: #374151 !important; }
          .gjs-three-bg { background-color: #f3f4f6 !important; }
          .gjs-four-color, .gjs-four-color-h:hover { color: #3b82f6 !important; }
          .selectors-container { background: #f9fafb !important; }
          .selectors-container .gjs-clm-sels-info,
          .selectors-container .gjs-clm-header,
          .selectors-container .gjs-clm-tags { background: #f9fafb !important; color: #374151 !important; }
          .selectors-container .gjs-clm-label,
          .selectors-container .gjs-sm-label { color: #6b7280 !important; }
          .gjs-clm-tags .gjs-clm-tag { background: #e5e7eb !important; color: #374151 !important; }
          .gjs-clm-tags .gjs-clm-tag.gjs-three-bg { background: #3b82f6 !important; color: white !important; }
          .gjs-clm-select, .gjs-sm-select {
            background: white !important; color: #374151 !important; border-color: #d1d5db !important;
          }
          .gjs-cv-canvas { background: #e5e7eb; }
          .gjs-cv-canvas-bg { background: #e5e7eb !important; }
          .gjs-frame-wrapper { background: white; }
          .gjs-field-color-picker .gjs-checker-bg { border: 1px solid #d1d5db; }
          .gjs-field-color-picker input { border: 1px solid #e5e7eb; }
          .sp-container { background: white !important; border: 1px solid #e5e7eb !important; }
          .gjs-rte-toolbar { background: #1f2937; }
          .gjs-toolbar { background: #1f2937; }
          .gjs-badge { background: #3b82f6; color: white; }
          .gjs-trt-trait .gjs-label { color: #6b7280; }
        `}
      `}</style>
    </div>
  );
}

export default WebsiteBuilderCanvas;
